import { CSSProperties } from 'react';
import { PhaseTypeEnum, JobMasterSheetInterface } from '@vatos-pas/common';
import { DRYWALL_PHASE_TYPE_TO_PRIORITY } from 'utils/phaseUtils';
import { WorkflowConfig, ColumnDef } from './types';

const EARN_MARK_PHASES: string[] = [
  PhaseTypeEnum.Spraying,
  PhaseTypeEnum.Bump1,
  PhaseTypeEnum.Bump2,
  PhaseTypeEnum.Bump3,
  PhaseTypeEnum.Bump4,
  PhaseTypeEnum.Bump5,
];

const getJobLateRowStyle = (
  record: Pick<
    JobMasterSheetInterface,
    'currentPhaseType' | 'currentPhaseContractorId' | 'currentPhaseApproved'
  >,
): CSSProperties | undefined => {
  const { currentPhaseType, currentPhaseContractorId, currentPhaseApproved } =
    record;

  const isEarnMarkPhase = EARN_MARK_PHASES.includes(currentPhaseType);

  if (isEarnMarkPhase && currentPhaseContractorId && !currentPhaseApproved) {
    return { backgroundColor: '#FFC107' };
  }

  return undefined;
};

const DRYWALL_COLUMNS: ColumnDef[] = [
  // -- Static columns --
  { type: 'supervisor', label: 'Supervisors' },
  { type: 'text', source: 'jobLot', label: 'Lot' },
  { type: 'text', source: 'jobBlock', label: 'Block' },
  {
    type: 'text',
    source: 'poId',
    label: 'PO Id',
    sortable: false,
    adminOnly: true,
    hideOnWarranty: true,
  },
  { type: 'text', source: 'builderName', label: 'Builder' },
  { type: 'text', source: 'subdivisionName', label: 'Subdivision' },
  { type: 'text', source: 'payableBoardsTotal', label: 'Payable Boards' },
  {
    type: 'text',
    source: 'payableBoardsReceiptTotal',
    label: 'Supervisor Count',
  },
  { type: 'phase-name', source: 'currentPhaseName', label: 'Current Phase' },
  { type: 'status', label: 'Status', hideOnWarranty: true },
  {
    type: 'number',
    source: 'currentPhaseDaysCount',
    label: 'Current Phase Days',
    hideOnWarranty: true,
  },
  {
    type: 'number',
    source: 'jobDaysCount',
    label: 'Job Days',
    hideOnWarranty: true,
  },

  // -- Hanging (non-warranty) --
  {
    type: 'phase-contractor',
    label: 'Hanger',
    hideOnWarranty: true,
    priority: DRYWALL_PHASE_TYPE_TO_PRIORITY[PhaseTypeEnum.Hanging],
    contractorField: 'hangPhaseContractorName',
    suggestedField: 'hangPhaseSuggestedContractorName',
    dateField: 'hangPhaseDateStart',
  },
  {
    type: 'phase-date',
    label: 'Hang Date',
    source: 'hangPhaseDateStart',
    hideOnWarranty: true,
    priority: DRYWALL_PHASE_TYPE_TO_PRIORITY[PhaseTypeEnum.Hanging],
    contractorField: 'hangPhaseContractorName',
    suggestedField: 'hangPhaseSuggestedContractorName',
  },

  // -- Finishing (non-warranty, inline editable date) --
  {
    type: 'phase-contractor',
    label: 'Finisher',
    hideOnWarranty: true,
    priority: DRYWALL_PHASE_TYPE_TO_PRIORITY[PhaseTypeEnum.Finishing],
    contractorField: 'finishPhaseContractorName',
    suggestedField: 'finishPhaseSuggestedContractorName',
    dateField: 'finishPhaseDateStart',
  },
  {
    type: 'editable-date',
    label: 'Finish Date',
    hideOnWarranty: true,
    priority: DRYWALL_PHASE_TYPE_TO_PRIORITY[PhaseTypeEnum.Finishing],
    contractorField: 'finishPhaseContractorName',
    suggestedField: 'finishPhaseSuggestedContractorName',
    dateSource: 'finishPhaseDateStart',
    jobPhaseIdField: 'finishJobPhaseId',
    customDateField: 'customFinishDateStart',
    historyTitle: 'Finish Date Change History',
  },

  // -- Spraying (non-warranty, inline editable date) --
  {
    type: 'phase-contractor',
    label: 'Sprayer',
    hideOnWarranty: true,
    priority: DRYWALL_PHASE_TYPE_TO_PRIORITY[PhaseTypeEnum.Spraying],
    contractorField: 'sprayPhaseContractorName',
    suggestedField: 'sprayPhaseSuggestedContractorName',
    dateField: 'sprayPhaseDateStart',
  },
  {
    type: 'editable-date',
    label: 'Spray Date',
    hideOnWarranty: true,
    priority: DRYWALL_PHASE_TYPE_TO_PRIORITY[PhaseTypeEnum.Spraying],
    contractorField: 'sprayPhaseContractorName',
    suggestedField: 'sprayPhaseSuggestedContractorName',
    dateSource: 'sprayPhaseDateStart',
    jobPhaseIdField: 'sprayJobPhaseId',
    customDateField: 'customSprayDateStart',
    historyTitle: 'Spray Date Change History',
  },

  // -- Bump1-5 (warranty only) --
  {
    type: 'phase-contractor',
    label: 'Bump1',
    warrantyOnly: true,
    priority: DRYWALL_PHASE_TYPE_TO_PRIORITY[PhaseTypeEnum.Bump1],
    contractorField: 'bump1PhaseContractorName',
    suggestedField: 'bump1PhaseSuggestedContractorName',
    dateField: 'bump1PhaseDateStart',
  },
  {
    type: 'phase-date',
    label: 'Bump1 Date',
    source: 'bump1PhaseDateStart',
    warrantyOnly: true,
    priority: DRYWALL_PHASE_TYPE_TO_PRIORITY[PhaseTypeEnum.Bump1],
    contractorField: 'bump1PhaseContractorName',
    suggestedField: 'bump1PhaseSuggestedContractorName',
  },
  {
    type: 'phase-contractor',
    label: 'Bump2',
    warrantyOnly: true,
    priority: DRYWALL_PHASE_TYPE_TO_PRIORITY[PhaseTypeEnum.Bump2],
    contractorField: 'bump2PhaseContractorName',
    suggestedField: 'bump2PhaseSuggestedContractorName',
    dateField: 'bump2PhaseDateStart',
  },
  {
    type: 'phase-date',
    label: 'Bump2 Date',
    source: 'bump2PhaseDateStart',
    warrantyOnly: true,
    priority: DRYWALL_PHASE_TYPE_TO_PRIORITY[PhaseTypeEnum.Bump2],
    contractorField: 'bump2PhaseContractorName',
    suggestedField: 'bump2PhaseSuggestedContractorName',
  },
  {
    type: 'phase-contractor',
    label: 'Bump3',
    warrantyOnly: true,
    priority: DRYWALL_PHASE_TYPE_TO_PRIORITY[PhaseTypeEnum.Bump3],
    contractorField: 'bump3PhaseContractorName',
    suggestedField: 'bump3PhaseSuggestedContractorName',
    dateField: 'bump3PhaseDateStart',
  },
  {
    type: 'phase-date',
    label: 'Bump3 Date',
    source: 'bump3PhaseDateStart',
    warrantyOnly: true,
    priority: DRYWALL_PHASE_TYPE_TO_PRIORITY[PhaseTypeEnum.Bump3],
    contractorField: 'bump3PhaseContractorName',
    suggestedField: 'bump3PhaseSuggestedContractorName',
  },
  {
    type: 'phase-contractor',
    label: 'Bump4',
    warrantyOnly: true,
    priority: DRYWALL_PHASE_TYPE_TO_PRIORITY[PhaseTypeEnum.Bump4],
    contractorField: 'bump4PhaseContractorName',
    suggestedField: 'bump4PhaseSuggestedContractorName',
    dateField: 'bump4PhaseDateStart',
  },
  {
    type: 'phase-date',
    label: 'Bump4 Date',
    source: 'bump4PhaseDateStart',
    warrantyOnly: true,
    priority: DRYWALL_PHASE_TYPE_TO_PRIORITY[PhaseTypeEnum.Bump4],
    contractorField: 'bump4PhaseContractorName',
    suggestedField: 'bump4PhaseSuggestedContractorName',
  },
  {
    type: 'phase-contractor',
    label: 'Bump5',
    warrantyOnly: true,
    priority: DRYWALL_PHASE_TYPE_TO_PRIORITY[PhaseTypeEnum.Bump5],
    contractorField: 'bump5PhaseContractorName',
    suggestedField: 'bump5PhaseSuggestedContractorName',
    dateField: 'bump5PhaseDateStart',
  },
  {
    type: 'phase-date',
    label: 'Bump5 Date',
    source: 'bump5PhaseDateStart',
    warrantyOnly: true,
    priority: DRYWALL_PHASE_TYPE_TO_PRIORITY[PhaseTypeEnum.Bump5],
    contractorField: 'bump5PhaseContractorName',
    suggestedField: 'bump5PhaseSuggestedContractorName',
  },

  // -- Pre-Rock + Submit --
  { type: 'pre-rock', label: 'Pre-Rock' },
  { type: 'submit-button', label: '', hideOnWarranty: true },
];

export const DRYWALL_CONFIG: WorkflowConfig = {
  phaseStructureType: 'Drywall',

  list: {
    columns: DRYWALL_COLUMNS,
    editableDateColumns: [
      { field: 'customFinishDateStart', label: 'Finish Date' },
      { field: 'customSprayDateStart', label: 'Spray Date' },
    ],
    bulkActionsEnabled: true,
    rowStyleFn: getJobLateRowStyle,
    statusColumnEnabled: true,
    preRockColumnEnabled: true,
    defaultSort: { field: 'hangPhaseDateStart', order: 'ASC' },
    pagination: { perPage: 50, rowsPerPageOptions: [5, 10, 25, 50] },
  },

  timeline: {
    sprayReadinessEnabled: true,
    bumpoutTimesheetDesktopBehaviors: true,
    goBackWarnings: {
      default: 'Are you sure that you want to go back?',
      bump: 'Are you sure you want to proceed? All notes associated with this Bump will be permanently removed.',
    },
    bumpRequestLabels: {
      garage: 'Log Garage',
      bumpDefault: 'Log BumpOut',
      bumpTimesheet: 'Request BumpOut',
    },
  },

  filters: {
    phaseStructureType: 'Drywall',
    priorityRange: { gte: 0, lte: 500 },
    warrantyPriorityRange: { gte: 600, lte: 1000 },
  },

  edit: {
    backUrl: '/job-master-sheet',
    warrantyUrlSegment: 'job-warranty-sheet',
    statusBarThreshold: 'gt200',
    mobileBreakpoint: '850px', // should probably be unified
    boardsMountStrategy: 'keepMounted', // should probably be unified
    tabBarColor: '#21a1f9',
  },

  email: {
    managementType: 'multi-supervisor',
  },

  tracking: {
    mixpanelEnabled: true,
  },
};
